import { useMemo, useState, useRef, useEffect } from "react";
import {
  Typography,
  useDataGrid,
  DataGrid,
  List,
  Button,
} from "@pankod/refine-mui";
import useLocales from "locales/useLocales";
import axios from "utils/axios";
import { toast } from "react-toastify";
import TranslateIcon from "@mui/icons-material/Translate";

export default function CustomList({
  title,
  columns,
  resource,
  canCreate,
  translateContentType,
}) {
  const { currentLang } = useLocales();
  const columsMemo = useMemo(() => columns, []);
  const [translating, setTranslating] = useState(false);
  const [progress, setProgress] = useState(null);
  const pollRef = useRef(null);

  // Clean up any pending poll timer when the component unmounts.
  useEffect(() => {
    return () => {
      if (pollRef.current) clearTimeout(pollRef.current);
    };
  }, []);

  const { dataGridProps, sortingMode, sortModel, onSortModelChange } =
    useDataGrid({
      metaData: {
        locale: currentLang?.value?.includes("ru")
          ? currentLang.value
          : currentLang.value.substring(0, 2),
        populate: "*",
      },
    });

  const pollStatus = async (jobId) => {
    try {
      const { data: status } = await axios.get(
        `/api/translate-all-status?jobId=${jobId}`,
      );

      if (status.status === "running") {
        setProgress({ processed: status.processed, total: status.total });
        pollRef.current = setTimeout(() => pollStatus(jobId), 3000);
        return;
      }

      setTranslating(false);
      setProgress(null);

      if (status.status === "done") {
        toast.success(
          `Translation complete: ${status.translated} translated, ${status.skipped} skipped, ${status.errors} errors`,
        );
      } else {
        toast.error("Translation failed: " + (status.error || "unknown error"));
      }
    } catch (error) {
      setTranslating(false);
      setProgress(null);
      toast.error(
        "Translation status check failed: " +
          (error.message || "unknown error"),
      );
    }
  };

  const handleTranslateAll = async () => {
    if (!translateContentType) return;
    setTranslating(true);
    setProgress(null);
    try {
      const { data } = await axios.post("/api/translate-all", {
        contentType: translateContentType,
      });
      toast.info("Translation started in the background...");
      pollStatus(data.jobId);
    } catch (error) {
      setTranslating(false);
      toast.error("Translation failed: " + (error.message || "unknown error"));
    }
  };

  const buttonLabel = translating
    ? progress && progress.total
      ? `Translating ${progress.processed}/${progress.total}...`
      : "Translating..."
    : "Translate All";

  return (
    <List
      resource={resource}
      createButtonProps={{ size: "small" }}
      canCreate={canCreate}
      headerProps={{
        title: <Typography variant="h5">{title}</Typography>,
        action: (
          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            {translateContentType && (
              <Button
                size="small"
                variant="outlined"
                color="primary"
                startIcon={<TranslateIcon />}
                disabled={translating}
                onClick={handleTranslateAll}
              >
                {buttonLabel}
              </Button>
            )}
          </div>
        ),
      }}
    >
      <DataGrid
        {...dataGridProps}
        columns={columsMemo}
        sortingMode={sortingMode}
        sortModel={sortModel}
        onSortModelChange={onSortModelChange}
        autoHeight
      />
    </List>
  );
}
